// Copy of the web app's src/helper/singleInterceptor.js (event-day job devices,
// card 540f21a4 / MC review 56b15e4d R1-F1); keep the two in step.
import { useEffect, useRef } from "react";

// Installs ONE axios response interceptor per client (payment-requests review
// R1-F3). AppWrapper used to call interceptors.response.use on every render,
// stacking a handler per render: the first handler could send an expired
// pay-link session to /login and the next one, now seeing /login, would fall
// through and navigate to "/".
//
// The interceptor is installed during the FIRST render (not in an effect) so a
// child component's first request is already covered, exactly as before. The
// registry lives at module level, keyed by client, because React may discard a
// first render's refs (StrictMode); the newest render's handlers are the ones
// that run, so each render's closures (navigate, state) are still current. It
// is ejected when the last component using it unmounts.
const registry = new WeakMap(); // client -> { id, users, current }

function ensure(client, current) {
  let entry = registry.get(client);
  if (!entry) {
    entry = { id: null, users: 0, current };
    entry.id = client.interceptors.response.use(
      (response) => entry.current.onFulfilled(response),
      (error) => entry.current.onRejected(error)
    );
    registry.set(client, entry);
  }
  return entry;
}

export function useSingleResponseInterceptor(client, onFulfilled, onRejected) {
  const latest = useRef(null);
  latest.current = { onFulfilled, onRejected };
  ensure(client, latest.current).current = latest.current;

  useEffect(() => {
    const entry = ensure(client, latest.current);
    entry.current = latest.current;
    entry.users += 1;
    return () => {
      entry.users -= 1;
      if (entry.users <= 0 && registry.get(client) === entry) {
        client.interceptors.response.eject(entry.id);
        registry.delete(client);
      }
    };
  }, [client]);
}
